import React, { useState } from 'react';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/common/Button';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCalendar, FiClock, FiUser, FiUsers, FiPlus, FiFilter, 
  FiChevronLeft, FiChevronRight, FiCheck, FiX, FiMoreVertical,
  FiPhone, FiMail, FiDollarSign, FiMapPin
} = FiIcons;

const BookingManager = () => {
  const [activeView, setActiveView] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showNewBookingForm, setShowNewBookingForm] = useState(false);

  const bookingTypes = [
    { id: 'portrait', name: 'Portrait Session', color: 'bg-blue-500' },
    { id: 'wedding', name: 'Wedding', color: 'bg-pink-500' },
    { id: 'family', name: 'Family Session', color: 'bg-green-500' },
    { id: 'commercial', name: 'Commercial', color: 'bg-purple-500' },
    { id: 'event', name: 'Event', color: 'bg-orange-500' }
  ];

  const bookings = [
    {
      id: 1,
      client: 'Emily Davis',
      email: 'emily@example.com',
      phone: '(555) 123-4567',
      type: 'portrait',
      date: '2024-01-15',
      startTime: '10:00 AM',
      endTime: '11:30 AM',
      location: 'Central Park, NY',
      status: 'confirmed',
      paid: true,
      amount: 250
    },
    {
      id: 2,
      client: 'Mike & Jessica',
      email: 'mikej@example.com',
      phone: '(555) 987-6543',
      type: 'wedding',
      date: '2024-01-18',
      startTime: '2:00 PM',
      endTime: '6:00 PM',
      location: 'Grand Hotel',
      status: 'pending',
      paid: false,
      amount: 1500
    },
    {
      id: 3,
      client: 'Anderson Family',
      email: 'andersons@example.com',
      phone: '(555) 456-7890',
      type: 'family',
      date: '2024-01-20',
      startTime: '11:00 AM',
      endTime: '12:30 PM',
      location: 'Beach Front',
      status: 'confirmed',
      paid: true,
      amount: 350
    },
    {
      id: 4,
      client: 'Tech Corp',
      email: 'marketing@techcorp.com',
      phone: '(555) 222-3333',
      type: 'commercial',
      date: '2024-01-22',
      startTime: '9:00 AM',
      endTime: '4:00 PM',
      location: 'Tech Corp HQ',
      status: 'confirmed',
      paid: true,
      amount: 1200
    }
  ];

  const getBookingTypeColor = (typeId) => {
    const bookingType = bookingTypes.find(type => type.id === typeId);
    return bookingType ? bookingType.color : 'bg-gray-500';
  };

  const getBookingTypeName = (typeId) => {
    const bookingType = bookingTypes.find(type => type.id === typeId);
    return bookingType ? bookingType.name : 'Unknown';
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  // Generate calendar days for the current month view
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    
    // Create an array for the days in the month
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();

  const getBookingsForDate = (date) => {
    return bookings.filter(booking => {
      const bookingDate = new Date(booking.date);
      return bookingDate.getDate() === date.getDate() &&
             bookingDate.getMonth() === date.getMonth() &&
             bookingDate.getFullYear() === date.getFullYear();
    });
  };

  const isSelectedDate = (date) => {
    if (!date) return false;
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  const isToday = (date) => {
    if (!date) return false;
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Booking Manager
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your photography sessions and client bookings
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <Button
              variant={activeView === 'calendar' ? 'primary' : 'outline'}
              size="sm"
              icon={FiCalendar}
              onClick={() => setActiveView('calendar')}
            >
              Calendar
            </Button>
            <Button
              variant={activeView === 'list' ? 'primary' : 'outline'}
              size="sm"
              icon={FiIcons.FiList}
              onClick={() => setActiveView('list')}
            >
              List
            </Button>
            <Button
              icon={FiPlus}
              onClick={() => setShowNewBookingForm(true)}
            >
              New Booking
            </Button>
          </div>
        </div>

        {/* Calendar View */}
        {activeView === 'calendar' && (
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Calendar Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatMonthYear(currentMonth)}
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={previousMonth}
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextMonth}
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <button
                onClick={() => setCurrentMonth(new Date())}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Today
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 text-center">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="py-2 border-b border-gray-200 dark:border-gray-700 font-medium text-gray-600 dark:text-gray-400">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {calendarDays.map((date, index) => (
                <div 
                  key={index} 
                  className={`min-h-[120px] p-1 border border-gray-200 dark:border-gray-700 ${
                    date ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750' : ''
                  } ${
                    isSelectedDate(date) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                  onClick={() => date && setSelectedDate(date)}
                >
                  {date && (
                    <>
                      <div className={`text-right p-1 ${
                        isToday(date) 
                          ? 'bg-primary-600 text-white rounded-full w-7 h-7 flex items-center justify-center ml-auto' 
                          : ''
                      }`}>
                        {date.getDate()}
                      </div>
                      
                      <div className="mt-1 space-y-1">
                        {getBookingsForDate(date).map((booking) => (
                          <div 
                            key={booking.id} 
                            className={`text-xs p-1 rounded truncate text-white ${getBookingTypeColor(booking.type)}`}
                            title={`${booking.client} - ${booking.startTime}`}
                          >
                            {booking.startTime} - {booking.client}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* List View */}
        {activeView === 'list' && (
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Filters */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-3">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
                  Status:
                </span>
                <select className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white px-3 py-1">
                  <option value="all">All</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
                  Type:
                </span>
                <select className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white px-3 py-1">
                  <option value="all">All Types</option>
                  {bookingTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
                  Date:
                </span>
                <select className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white px-3 py-1">
                  <option value="all">All Dates</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="past">Past</option>
                  <option value="thisMonth">This Month</option>
                  <option value="nextMonth">Next Month</option>
                </select>
              </div>
              <Button variant="outline" size="sm" icon={FiFilter}>
                More Filters
              </Button>
            </div>
            
            {/* Bookings Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {bookings.map((booking) => (
                    <tr 
                      key={booking.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-750"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {booking.client}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                              <span className="flex items-center">
                                <SafeIcon icon={FiMail} className="w-3 h-3 mr-1" />
                                {booking.email}
                              </span>
                              <span className="hidden md:flex items-center">
                                <SafeIcon icon={FiPhone} className="w-3 h-3 mr-1" />
                                {booking.phone}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <span className={`w-3 h-3 rounded-full mr-2 ${getBookingTypeColor(booking.type)}`} />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {getBookingTypeName(booking.type)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {new Date(booking.date).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                          <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                          {booking.startTime} - {booking.endTime}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-900 dark:text-white flex items-center">
                          <SafeIcon icon={FiMapPin} className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate max-w-[150px]">{booking.location}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(booking.status)}`}>
                          {booking.status === 'confirmed' && <SafeIcon icon={FiCheck} className="w-3 h-3 mr-1" />}
                          {booking.status === 'pending' && <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />}
                          {booking.status === 'cancelled' && <SafeIcon icon={FiX} className="w-3 h-3 mr-1" />}
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <SafeIcon 
                            icon={booking.paid ? FiCheck : FiClock} 
                            className={`w-4 h-4 mr-1 ${booking.paid ? 'text-green-500' : 'text-yellow-500'}`} 
                          />
                          <span className="text-sm text-gray-900 dark:text-white">
                            ${booking.amount}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                          <SafeIcon icon={FiMoreVertical} className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Selected Day Details (for Calendar View) */}
        {activeView === 'calendar' && (
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
              {getBookingsForDate(selectedDate).length > 0 && (
                <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-400 text-xs font-medium py-1 px-2 rounded-full">
                  {getBookingsForDate(selectedDate).length} bookings
                </span>
              )}
            </div>
            
            {getBookingsForDate(selectedDate).length > 0 ? (
              <div className="space-y-4">
                {getBookingsForDate(selectedDate).map((booking) => (
                  <div 
                    key={booking.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-750"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center">
                          <span className={`w-3 h-3 rounded-full mr-2 ${getBookingTypeColor(booking.type)}`} />
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {booking.client}
                          </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                          {getBookingTypeName(booking.type)}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(booking.status)}`}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="flex items-center">
                        <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {booking.startTime} - {booking.endTime}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                          {booking.location}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {booking.email}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {booking.phone}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center">
                        <SafeIcon 
                          icon={booking.paid ? FiCheck : FiDollarSign} 
                          className={`w-4 h-4 mr-1 ${booking.paid ? 'text-green-500' : 'text-yellow-500'}`} 
                        />
                        <span className="text-sm font-medium">
                          {booking.paid ? 'Paid' : 'Awaiting Payment'}: ${booking.amount}
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                          Edit
                        </Button>
                        <Button size="sm">
                          Details
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCalendar} className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No bookings scheduled
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  There are no bookings scheduled for this date. Would you like to create one?
                </p>
                <Button 
                  icon={FiPlus}
                  onClick={() => setShowNewBookingForm(true)}
                >
                  New Booking
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Booking Form Modal (simplified version) */}
      {showNewBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Create New Booking
                </h2>
                <button 
                  onClick={() => setShowNewBookingForm(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                Form fields for creating a new booking would go here. This is a simplified placeholder.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <Button 
                variant="outline"
                onClick={() => setShowNewBookingForm(false)}
              >
                Cancel
              </Button>
              <Button onClick={() => setShowNewBookingForm(false)}>
                Create Booking
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default BookingManager;